import React, { createContext, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useGrowthBook } from '@growthbook/growthbook-react'

import {
  selectCurrentVariantSteps,
  selectGrowthBookDefaultValues,
} from 'root-redux/selects/common'

import { useBulkSelector } from 'hooks/common/useBulkSelector'

import { IStep } from 'models/variant.model'

import { PAGE_TYPE, POST_PAYMENT_PAGES } from 'root-constants/common'
import { PageId } from 'root-constants/pages'

type TPageInfoContextProps = {
  steps: IStep[]
  isFirstPage: boolean
  isSecondPage: boolean
  isDownloadPage: boolean
  isPostPaymentPage: boolean
  isPaymentPage: boolean
  isSecondUpsellPage: boolean
  hasHeader: boolean
  hasProgressbar: boolean
  currentSubscriptionPageId: PageId
  currentPaymentPageId: PageId
  currentUpsellPageId: PageId
  currentPageId: PageId
  currentPageIndex: number
  currentPresalePageId: PageId
}

const PageInfoContext = createContext<TPageInfoContextProps | null>(null)

const useSelectors = () =>
  useBulkSelector({
    steps: selectCurrentVariantSteps,
    growthBookDefaultValues: selectGrowthBookDefaultValues,
  })

export const PageInfoProvider = ({ children }) => {
  const { pathname } = useLocation()
  const growthBook = useGrowthBook()
  const { steps, growthBookDefaultValues } = useSelectors()

  const unnecessaryPages = (() => {
    if (!growthBook?.ready) return []
    return Object.entries(growthBookDefaultValues).reduce(
      (acc, [key, defaultValues]) => {
        const experimentValue = growthBook.getFeatureValue(
          key,
          JSON.stringify(defaultValues),
        )
        return [...acc, ...JSON.parse(experimentValue)]
      },
      [] as string[],
    )
  })()

  const filteredSteps = useMemo(() => {
    return unnecessaryPages.length
      ? steps.filter(({ id }) => !unnecessaryPages.includes(id))
      : steps
  }, [steps, unnecessaryPages])

  const currentPageId = pathname.slice(1) as PageId
  const currentPageIndex = filteredSteps.findIndex(
    ({ id }) => id === currentPageId,
  )

  const isFirstPage =
    filteredSteps.findIndex(({ id }) => id === currentPageId) === 0

  const isSecondPage =
    filteredSteps.findIndex(({ id }) => id === currentPageId) === 1

  const isDownloadPage = currentPageId === PageId.DOWNLOAD

  const isPostPaymentPage = POST_PAYMENT_PAGES.includes(currentPageId as PageId)

  const { hasHeader, hasProgressbar } = {
    hasHeader: steps[currentPageIndex]?.hasHeader || isPostPaymentPage,
    hasProgressbar: steps[currentPageIndex]?.hasProgressBar,
  }

  let currentPresalePageId = '' as PageId
  let currentSubscriptionPageId = '' as PageId
  let currentPaymentPageId = '' as PageId
  let currentUpsellPageId = '' as PageId

  filteredSteps.forEach(
    ({ isSubscriptions, isPayment, isUpsell, id, pageType }) => {
      if (pageType === PAGE_TYPE.PRESALE) {
        currentPresalePageId = id
      }

      if (isSubscriptions && !currentSubscriptionPageId) {
        currentSubscriptionPageId = id
      }

      if (isPayment && !currentPaymentPageId) currentPaymentPageId = id
      if (isUpsell && !currentUpsellPageId) currentUpsellPageId = id
    },
  )

  const isPaymentPage = filteredSteps[currentPageIndex]?.isPayment

  const isSecondUpsellPage = currentPageId === PageId.UPSELL_2

  const value = useMemo(
    () => ({
      steps: filteredSteps,
      isFirstPage,
      isSecondPage,
      isDownloadPage,
      isPostPaymentPage,
      isPaymentPage,
      isSecondUpsellPage,
      hasHeader,
      hasProgressbar,
      currentPageId,
      currentPageIndex,
      currentSubscriptionPageId,
      currentPaymentPageId,
      currentUpsellPageId,
      currentPresalePageId,
    }),
    [
      filteredSteps,
      isFirstPage,
      isSecondPage,
      isDownloadPage,
      isPostPaymentPage,
      isPaymentPage,
      isSecondUpsellPage,
      hasHeader,
      hasProgressbar,
      currentPageId,
      currentPageIndex,
      currentSubscriptionPageId,
      currentPaymentPageId,
      currentUpsellPageId,
      currentPresalePageId,
    ],
  )

  return (
    <PageInfoContext.Provider value={value}>
      {children}
    </PageInfoContext.Provider>
  )
}

export const usePageInfo = () => {
  const context = useContext(PageInfoContext)
  if (!context) {
    throw new Error('usePageInfo must be used within a PageInfoProvider')
  }
  return context
}
